<script setup lang="ts">
/**
 * Blog listing page — Story 115-122
 *
 * Responsibilities:
 *   - Fetch all published blog articles from the `blog` content collection
 *   - Mount BlogSearchBox component (T002) and wire useBlogSearch composable (T001)
 *   - Handle ?search= URL query parameter: initialise on mount (SC8), update on submit (SC2),
 *     remove on clear (SC8) — using router.replace to preserve back-navigation (R-006)
 *   - Preserve all existing query params (?product=, ?category=, ?page=) when adding/removing
 *     ?search= (R-003)
 *   - T004: Read ?product= and ?category= from route.query inside computed() (L146),
 *     apply filter predicate on allArticles FIRST (performance: R-003), then intersect
 *     with searchResults for AND logic (SC6). Result label reflects combined state (SC2).
 *
 * SSG-safe patterns (L145, L146):
 *   - All side effects in onMounted
 *   - route.query access inside computed() or onMounted() — never at module/setup level
 */
definePageMeta({ title: 'Blog | QxAIOS' })

import { useBlogFilterState, useBlogSearch } from '~/composables/useBlogSearch'
import { useAnalytics } from '../../../composables/useAnalytics'
import { useBlogContent } from '../../../composables/useBlogContent'
import { useBlogTelemetry } from '../../../composables/useBlogTelemetry'
import { DEFAULT_BLOG_PAGE_SIZE, parsePageQueryParam, useBlogPagination } from '../../../composables/useBlogPagination'
import { useAsyncData, useRoute, useRouter } from '#imports'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import type { BlogArticle } from '../../../types/blog'

const PAGE_TITLE = 'Blog | QxAIOS'
const PAGE_DESCRIPTION = 'Read QxAIOS insights on GMP quality review, verification workflows, and compliance operations.'
const CANONICAL_URL = 'https://qx.qiksolve.com/blog'
const SOCIAL_IMAGE = 'https://qx.qiksolve.com/images/logo.svg'

useSeoMeta({
  title: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  ogTitle: PAGE_TITLE,
  ogDescription: PAGE_DESCRIPTION,
  ogType: 'website',
  ogUrl: CANONICAL_URL,
  ogImage: SOCIAL_IMAGE,
  twitterCard: 'summary_large_image',
  twitterTitle: PAGE_TITLE,
  twitterDescription: PAGE_DESCRIPTION,
  twitterImage: SOCIAL_IMAGE,
  canonical: CANONICAL_URL,
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: CANONICAL_URL,
    },
  ],
})

const route = useRoute()
const router = useRouter()
const BLOG_PAGE_SIZE = DEFAULT_BLOG_PAGE_SIZE
const { queryPublishedArticles } = useBlogContent()

const {
  emitBlogSearchSubmitted,
  emitBlogSearchNoResults,
  emitBlogSearchResultClicked,
  emitBlogFilterApplied,
  emitBlogFilterCleared,
} = useAnalytics()
const {
  trackBlogListingViewed,
  trackBlogArticleClicked,
  trackBlogPaginationClicked,
  startBlogEngagementTracking,
} = useBlogTelemetry()

const {
  searchQuery,
  searchResults,
  isSearching,
  resultCount,
  hasSearch,
  performSearch,
  clearSearch,
  reportResultClicked,
} = useBlogSearch({
  onSearchCompleted: (query: string, count: number, responseTimeMs: number) => {
    // L145: callback fires inside performSearch's try block — event handler context ✅
    const filters = (route.query.product || route.query.category)
      ? { product: route.query.product as string | undefined, category: route.query.category as string | undefined }
      : null
    emitBlogSearchSubmitted(query, count, filters, responseTimeMs)
  },
  onNoResults: (query: string) => {
    const filters = (route.query.product || route.query.category)
      ? { product: route.query.product as string | undefined, category: route.query.category as string | undefined }
      : null
    emitBlogSearchNoResults(query, filters)
  },
  onResultClicked: (query: string, articleId: string, position: number) => {
    emitBlogSearchResultClicked(query, articleId, position)
  },
})

// Fetch ALL published articles for the listing (newest first)
const { data: allArticles } = await useAsyncData('blog-articles', () =>
  queryPublishedArticles(),
)

const {
  selectedProduct,
  selectedCategory,
  availableProducts,
  availableCategories,
  hasActiveFilters: hasFilters,
  filteredArticles,
  setFilters,
  clearFilters,
} = useBlogFilterState(allArticles)

// ---------------------------------------------------------------------------
// Search event handlers
// ---------------------------------------------------------------------------

/**
 * SC2: Update URL to /blog?search=<query> on submit.
 * router.replace (not push) preserves back-navigation behaviour (R-006, SC8).
 * Preserves all existing query params — ?product=, ?category=, ?page= (R-003).
 */
async function handleSearch(query: string): Promise<void> {
  await router.replace({
    query: {
      ...route.query,
      search: query || undefined,
      page: undefined,
    },
  })
  if (query.trim()) {
    await performSearch(query)
  } else {
    clearSearch()
  }
}

/**
 * SC8: Remove ?search= and ?page= from URL on clear.
 * router.replace (not push) — correct back-navigation (R-006, SC8).
 */
async function handleClear(): Promise<void> {
  const query = { ...route.query }
  delete query.search
  delete query.page
  await router.replace({ query })
  clearSearch()
}

// ---------------------------------------------------------------------------
// SC8: Initialise search from ?search= URL param on client mount (L145)
// Side effects in onMounted — never at module/setup scope (L146)
// ---------------------------------------------------------------------------
onMounted(async () => {
  // L146: route.query accessed inside lifecycle hook, not at setup/module level
  const urlSearch = route.query.search
  if (urlSearch && typeof urlSearch === 'string' && urlSearch.trim()) {
    await performSearch(urlSearch)
  }
})

// Step 2: SC6 — AND logic: search runs on the already-filtered set
// When search is active: return searchResults filtered to only those in filteredArticles
// When no search: return filteredArticles directly
//
// Note: performSearch always searches all articles (by design in useBlogSearch).
// We intersect its results with filteredArticles via a path Set for O(1) lookup.
const displayedArticles = computed<BlogArticle[]>(() => {
  if (hasSearch.value) {
    const filteredByPath = new Map(filteredArticles.value.map((article: BlogArticle) => [article.path, article] as const))
    return searchResults.value
      .map((result: { path: string }) => filteredByPath.get(result.path))
      .filter((article: BlogArticle | undefined): article is BlogArticle => Boolean(article))
  }
  return filteredArticles.value
})

const pageQuery = computed(() => route.query.page)
const pagination = useBlogPagination(displayedArticles, {
  pageQuery,
  pageSize: BLOG_PAGE_SIZE,
})

const pagedArticles = computed(() => pagination.pagedItems.value)
const currentPage = computed(() => pagination.currentPage.value)
const totalPages = computed(() => pagination.totalPages.value)
const hasPrevPage = computed(() => pagination.hasPrev.value)
const hasNextPage = computed(() => pagination.hasNext.value)
const pageNumbers = computed(() => pagination.pageNumbers.value)
const totalArticleCount = computed(() => allArticles.value?.length ?? 0)

/** Human-readable result count label for the summary line (SC2 — reflects combined state) */
const resultLabel = computed(() => {
  const total = displayedArticles.value.length
  if (isSearching.value) return 'Searching...'
  if (hasSearch.value && hasFilters.value) {
    return `Found ${total} article${total !== 1 ? 's' : ''} matching '${searchQuery.value}'${selectedProduct.value ? ` in ${selectedProduct.value}` : ''}${selectedCategory.value ? ` / ${selectedCategory.value}` : ''}`
  }
  if (hasSearch.value) {
    return `Found ${total} article${total !== 1 ? 's' : ''} matching '${searchQuery.value}'`
  }
  if (hasFilters.value) {
    return `${total} article${total !== 1 ? 's' : ''} filtered`
  }
  return `${totalArticleCount.value} articles`
})

function buildQueryForPage(page: number) {
  return {
    ...route.query,
    page: page > 1 ? String(page) : undefined,
  }
}

async function handlePageChange(targetPage: number): Promise<void> {
  const fromPage = pagination.currentPage.value
  const nextPage = pagination.clampPage(targetPage)
  if (nextPage === fromPage) return

  trackBlogPaginationClicked('/blog', fromPage, nextPage, pagination.totalPages.value)
  await router.replace({ query: buildQueryForPage(nextPage) })
  if (import.meta.client) {
    globalThis.scrollTo?.({ top: 0, behavior: 'smooth' })
  }
}

async function handleFilterChange(payload: { product: string | null; category: string | null }): Promise<void> {
  setFilters(payload)
  await router.replace({
    query: {
      ...route.query,
      product: payload.product || undefined,
      category: payload.category || undefined,
      page: undefined,
    },
  })

  emitBlogFilterApplied({
    product: payload.product,
    category: payload.category,
  })
}

async function handleClearFilters(): Promise<void> {
  clearFilters()
  await router.replace({
    query: {
      ...route.query,
      product: undefined,
      category: undefined,
      page: undefined,
    },
  })
  emitBlogFilterCleared()
}

function extractSlugFromPath(path: string): string {
  return path.replace(/^\/blog\//, '').replace(/\/$/, '')
}

function formatDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed)
}

function handleArticleClick(article: BlogArticle, index: number): void {
  const articlePosition = ((pagination.currentPage.value - 1) * pagination.pageSize) + index + 1
  reportResultClicked(article.path, articlePosition - 1)
  trackBlogArticleClicked(
    article.path,
    extractSlugFromPath(article.path),
    article.title,
    articlePosition,
    pagination.currentPage.value,
  )
}

let stopEngagementTracking: (() => void) | null = null

onMounted(() => {
  watch(
    () => route.query.page,
    async (rawPage) => {
      const parsed = parsePageQueryParam(rawPage)
      const clamped = pagination.currentPage.value
      const hasExplicitPage = rawPage !== undefined

      if (!hasExplicitPage && clamped === 1) return
      if (parsed === clamped && !(hasExplicitPage && clamped === 1)) return

      await router.replace({ query: buildQueryForPage(clamped) })
    },
    { immediate: true },
  )
})

onMounted(() => {
  trackBlogListingViewed('/blog', 'Blog | QxAIOS', displayedArticles.value.length, pagination.currentPage.value)
  stopEngagementTracking = startBlogEngagementTracking({
    pagePath: '/blog',
    pageType: 'listing',
  })
})

onBeforeUnmount(() => {
  stopEngagementTracking?.()
  stopEngagementTracking = null
})

/** Author initials from name — max 2 chars */
function authorInitials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}
</script>

<template>
  <div class="blog-listing bg-white">
    <!-- Page header bar (mirrors OD nav section) -->
    <div class="sticky top-[var(--header-height,0px)] z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
      <div
        class="mx-auto flex max-w-7xl items-center justify-between gap-5 px-6 py-0"
        style="height: 66px"
      >
        <span class="shrink-0 text-xs font-semibold uppercase tracking-widest text-spruce-800">
          QxAIOS Insights
        </span>
        <!-- Inline search + filter (BlogSearchBox) -->
        <div class="w-full max-w-[560px]">
          <BlogSearchBox
            :search-query="searchQuery"
            :result-count="resultCount"
            :has-search="hasSearch"
            :is-searching="isSearching"
            :selected-product="selectedProduct"
            :selected-category="selectedCategory"
            :product-options="availableProducts"
            :category-options="availableCategories"
            @search="handleSearch"
            @clear="handleClear"
            @filter-change="handleFilterChange"
            @clear-filters="handleClearFilters"
          />
        </div>
      </div>
      <!-- Subrow: active filter chips + result count -->
      <div class="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 pb-2.5">
        <div class="flex min-h-6 flex-wrap items-center gap-2">
          <span
            v-if="selectedProduct"
            class="inline-flex items-center gap-1 rounded-full border border-spruce-200 bg-spruce-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-spruce-800"
          >
            Product: {{ selectedProduct }}
          </span>
          <span
            v-if="selectedCategory"
            class="inline-flex items-center gap-1 rounded-full border border-spruce-200 bg-spruce-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-spruce-800"
          >
            Category: {{ selectedCategory }}
          </span>
        </div>
        <p class="shrink-0 text-[13px] text-gray-500">
          {{ resultLabel }}
        </p>
      </div>
    </div>

    <!-- Articles section -->
    <section class="px-6 py-6 sm:py-8">
      <div class="mx-auto max-w-7xl">
        <!-- Article grid -->
        <div
          v-if="pagedArticles.length > 0"
          id="blog-article-list"
          class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
        >
          <article
            v-for="(article, index) in pagedArticles"
            :key="article.path"
            class="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md"
            :class="index === 0 ? 'md:col-span-2 xl:col-span-3 md:flex-row' : ''"
            @click="handleArticleClick(article, index)"
          >
            <!-- Image area -->
            <NuxtLink
              :to="article.path"
              class="block focus-visible:outline-none"
              :class="index === 0 ? 'md:w-[300px] md:shrink-0' : ''"
              tabindex="-1"
            >
              <img
                v-if="article.heroImage"
                :src="article.heroImage"
                :alt="`${article.title} featured image`"
                class="w-full object-cover"
                :class="index === 0 ? 'h-full min-h-[180px]' : 'h-[180px]'"
                loading="lazy"
                decoding="async"
              >
              <div
                v-else
                class="flex items-center justify-center bg-gradient-to-b from-spruce-50 to-white"
                :class="index === 0 ? 'h-full min-h-[180px]' : 'h-[180px]'"
              >
                <svg
                  :width="index === 0 ? 64 : 48"
                  :height="index === 0 ? 64 : 48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  class="text-gray-400 opacity-20"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    x="2"
                    y="3"
                    width="20"
                    height="14"
                    rx="2"
                  />
                  <line
                    x1="8"
                    y1="21"
                    x2="16"
                    y2="21"
                  />
                  <line
                    x1="12"
                    y1="17"
                    x2="12"
                    y2="21"
                  />
                </svg>
              </div>
            </NuxtLink>

            <!-- Card body -->
            <div class="flex flex-1 flex-col p-[16px_18px_18px]">
              <!-- Badge row -->
              <div class="mb-2.5 flex flex-wrap items-center gap-1.5">
                <span
                  v-if="index === 0"
                  class="inline-flex items-center rounded-full bg-spruce-800 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white"
                >
                  Featured
                </span>
                <span
                  v-if="article.product"
                  class="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                >
                  {{ article.product }}
                </span>
                <span
                  v-for="cat in (article.categories ?? []).slice(0, 1)"
                  :key="cat"
                  class="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                >
                  {{ cat }}
                </span>
                <span class="ml-auto shrink-0 text-xs text-gray-400">{{ article.readingTime }} min read</span>
              </div>

              <!-- Title -->
              <h2
                class="font-semibold leading-snug text-gray-900 transition group-hover:text-spruce-800"
                :class="index === 0 ? 'mb-2 text-xl tracking-tight' : 'mb-1.5 text-base'"
              >
                <NuxtLink
                  :to="article.path"
                  class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-spruce-500"
                >
                  {{ article.title }}
                </NuxtLink>
              </h2>

              <!-- Excerpt -->
              <p
                class="flex-1 text-sm leading-relaxed text-gray-600"
                :class="index === 0 ? 'line-clamp-3' : 'line-clamp-2'"
              >
                {{ article.excerpt }}
              </p>

              <!-- Card footer -->
              <div class="mt-3.5 flex items-center gap-2 border-t border-gray-100 pt-3">
                <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-spruce-50 text-[11px] font-bold text-spruce-800">
                  {{ authorInitials(article.author.name) }}
                </div>
                <p class="flex-1 truncate text-xs text-gray-500">
                  <strong class="font-semibold text-gray-700">{{ article.author.name }}</strong>
                  <template v-if="article.author.title">
                    · {{ article.author.title }}
                  </template>
                </p>
                <time class="shrink-0 text-xs tabular-nums text-gray-400">{{ formatDate(article.publishedAt) }}</time>
              </div>
            </div>
          </article>
        </div>

        <!-- Empty state -->
        <div
          v-if="pagedArticles.length === 0"
          class="col-span-full mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-10 py-20 text-center"
        >
          <svg
            class="mx-auto mb-4 h-12 w-12 text-gray-300"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <p class="text-base font-semibold text-gray-500">
            No articles match your filters
          </p>
          <p class="mt-1 text-sm text-gray-400">
            Try adjusting your search or clearing the filters.
          </p>
        </div>

        <!-- Pagination -->
        <nav
          v-if="totalPages > 1"
          class="mt-8 flex items-center justify-center gap-1.5 border-t border-gray-200 pt-10"
          aria-label="Blog pagination"
          aria-controls="blog-article-list"
        >
          <UButton
            type="button"
            color="neutral"
            variant="ghost"
            class="flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-sm font-medium text-gray-600 transition hover:border-gray-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!hasPrevPage"
            aria-label="Go to previous page"
            @click="handlePageChange(currentPage - 1)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </UButton>

          <UButton
            v-for="pageNumber in pageNumbers"
            :key="pageNumber"
            type="button"
            color="neutral"
            :variant="pageNumber === currentPage ? 'solid' : 'ghost'"
            class="flex h-9 min-w-9 items-center justify-center rounded-lg border text-sm font-medium transition"
            :class="pageNumber === currentPage
              ? 'border-spruce-800 bg-spruce-800 font-bold text-white'
              : 'border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50'"
            :aria-current="pageNumber === currentPage ? 'page' : undefined"
            @click="handlePageChange(pageNumber)"
          >
            {{ pageNumber }}
          </UButton>

          <UButton
            type="button"
            color="neutral"
            variant="ghost"
            class="flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-sm font-medium text-gray-600 transition hover:border-gray-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!hasNextPage"
            aria-label="Go to next page"
            @click="handlePageChange(currentPage + 1)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </UButton>
        </nav>
      </div>
    </section>
  </div>
</template>
